import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getQuestHistory } from '../../services/writingService';
import { QuestProgress, GateType } from '../../types/writing';
import './WritingHistoryPage.css';
import { BilingualTitle } from '../../components/common/BilingualTitle';

// Extended type for history items with course info
interface QuestHistoryItem extends QuestProgress {
  type?: 'course_review' | 'standalone';
  courseName?: string;
  courseTitles?: Record<string, string>;
  courseLevel?: string;
  courseThumbnail?: string;
}

// Type guard to validate QuestHistoryItem
function isQuestHistoryItem(item: unknown): item is QuestHistoryItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'gateProgress' in item
  );
}

type FilterType = 'all' | 'course_review' | 'standalone';

// CEFR level color mapping
const CEFR_COLORS: Record<string, { bg: string; text: string }> = {
  A1: { bg: '#dcfce7', text: '#166534' },
  A2: { bg: '#d1fae5', text: '#047857' },
  B1: { bg: '#dbeafe', text: '#1d4ed8' },
  B2: { bg: '#c7d2fe', text: '#4338ca' },
  C1: { bg: '#e9d5ff', text: '#7c3aed' },
  C2: { bg: '#fce7f3', text: '#be185d' },
};

const WritingHistoryPage: React.FC = () => {
  const { t, i18n } = useTranslation('writing');
  const [sessions, setSessions] = useState<QuestHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getQuestHistory(PAGE_SIZE, page * PAGE_SIZE);
      const quests = data.quests || [];
      setSessions(quests.filter(isQuestHistoryItem));
      setTotal(data.total || 0);
    } catch (err: any) {
      console.error('Quest History error:', err);
      setError(err.message || 'Failed to load quest history');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filteredSessions = sessions.filter((s) => {
    if (filter === 'all') return true;
    if (filter === 'course_review') return s.type === 'course_review' || s.courseId;
    if (filter === 'standalone') return s.type === 'standalone' || !s.courseId;
    return true;
  });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    return 'needs-work';
  };

  const getGateLabel = (gateType: GateType): string => {
    // Use i18n keys for gate names
    const gateKeys: Record<GateType, string> = {
      translation: 'translateToEnglish',
      combine: 'sentenceCombining',
      expansion: 'paragraphExpansion',
      essay: 'essayWriting',
    };
    return t(gateKeys[gateType]);
  };

  const getAverageScore = (scores: Record<string, number | undefined>): number => {
    const validScores = Object.values(scores).filter((s): s is number => s !== undefined);
    if (validScores.length === 0) return 0;
    return Math.round(validScores.reduce((a, b) => a + b, 0) / validScores.length);
  };

  const getCompletedGatesCount = (gateProgress: Record<string, string>): number => {
    return Object.values(gateProgress).filter(status => status === 'completed').length;
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="quest-history-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quest-history-page">
      <div className="page-header">
        <div>
          <h1>{t('title')} - {t('historyTitle')}</h1>
          <p className="subtitle">{t('historySubtitle')}</p>
        </div>
        <Link to="/writing" className="back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {t('backToQuest')}
        </Link>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="filter-bar">
        {(['all', 'course_review', 'standalone'] as FilterType[]).map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? t('filterAll') : f === 'course_review' ? t('filterCourse') : t('filterPersonal')}
          </button>
        ))}
      </div>

      {filteredSessions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3>{t('emptyTitle')}</h3>
          <p>{t('emptyDescription')}</p>
          <Link to="/writing" className="start-btn">{t('startQuest')}</Link>
        </div>
      ) : (
        <>
          <div className="sessions-list">
            {filteredSessions.map((session) => {
              const avgScore = getAverageScore(session.scores || {});
              const completedGates = getCompletedGatesCount(session.gateProgress || {});
              const isCourseQuest = session.type !== 'standalone' && session.courseId;

              return (
                <Link
                  key={session.id}
                  to={`/writing/history/${session.id}`}
                  className="session-card"
                >
                  {/* Header: Course info or Personal Practice */}
                  <div className="session-header">
                    <div className="session-info">
                      {isCourseQuest ? (
                        <>
                          <BilingualTitle
                            title={session.courseName || ''}
                            titles={session.courseTitles}
                            fallback={t('courseQuest')}
                            variant="card"
                            as="h3"
                            className="course-title"
                          />
                          {session.courseLevel && (
                            <span
                              className="cefr-badge"
                              style={{
                                backgroundColor: CEFR_COLORS[session.courseLevel]?.bg || '#f3f4f6',
                                color: CEFR_COLORS[session.courseLevel]?.text || '#666',
                              }}
                            >
                              {session.courseLevel}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <h3 className="course-title">{t('personalPractice')}</h3>
                          <span className="personal-badge">{t('selfStudy')}</span>
                        </>
                      )}
                    </div>
                    <div className="session-meta-top">
                      <span className="session-date">{formatDate(session.completedAt || session.createdAt)}</span>
                      <span className="gates-completed">{t('gatesCompleted', { count: completedGates })}</span>
                    </div>
                  </div>

                  {/* Gate Scores - Large and Clear */}
                  <div className="gate-scores-grid">
                    {(['translation', 'combine', 'expansion', 'essay'] as GateType[]).map((gate) => {
                      const score = session.scores?.[gate];
                      const progress = session.gateProgress?.[gate];
                      const isCompleted = progress === 'completed';
                      const hasScore = score !== undefined;

                      return (
                        <div
                          key={gate}
                          className={`gate-score-card ${isCompleted ? 'completed' : 'incomplete'} ${hasScore ? getScoreColor(score) : ''}`}
                        >
                          <div className="gate-label">{getGateLabel(gate)}</div>
                          <div className="gate-score-value">
                            {hasScore ? (
                              <span className="score-number">{score}</span>
                            ) : (
                              <span className="no-score">—</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer: Average score and XP */}
                  <div className="session-footer">
                    <div className="avg-score-section">
                      <span className="avg-label">{t('averageScore')}</span>
                      <span className={`avg-value ${getScoreColor(avgScore)}`}>{avgScore}</span>
                    </div>
                    <div className="xp-section">
                      <span className="xp-value">+{session.totalXp || 0} XP</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {total > PAGE_SIZE && (
            <div className="pagination">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t('previous')}
              </button>
              <span className="page-info">
                {page + 1} / {Math.ceil(total / PAGE_SIZE)}
              </span>
              <button
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WritingHistoryPage;
